import {
  Autocomplete,
  Avatar,
  Button,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Box, Stack } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import dayjs from "dayjs";
import {
  isEmpty,
  validateDateFormat,
  validateText,
} from "../../../../../../utils/actionItemsValidation";
import { useSelector } from "react-redux";
import {
  selectAllUsers,
  selectUserToken,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import { ActionItemsEndPointLogic } from "../../../../../../logic/AssetManagement/ActionItems/actionItemsEndPointLogic";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import StatusHistoryLog from "./StatusHistoryLog";
import { PriorityOptions } from "../../../../../../constants/PriorityOption";
import { capitalizeEachWords } from "../../../../../../utils/helpers";

const initialData = {
  target: "",
  description: "",
  priority: "",
  due_date: "",
  assignee: "",
  current_status: "",
  statusActionDate: "",
};

const QualityScore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { singleactionItemDetails, description } = useSelector(
    (state: any) => state?.actionItems
  );
  const allUsers = useSelector(selectAllUsers);
  const oauthTokenHeader = useSelector(selectUserToken) as string;

  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  let siteName = null;

  const [singleActionItemData, setSingleActionItemData] = useState<any>({});
  const [formError, setFormError] = useState<any>(initialData);

  const [editActionItemData, setEditActionItemData] = useState<any>({});

  //-- Set Single Action item data to singleActionItemData
  useEffect(() => {
    if (
      singleactionItemDetails &&
      Object.keys(singleactionItemDetails).length > 0
    )
      setSingleActionItemData({
        reference_id: singleactionItemDetails?.reference_id,
        shared_uid: singleactionItemDetails?.shared_uid,
        action_item: singleactionItemDetails?.data?.action_item,
        category: singleactionItemDetails?.data?.category,
        target: singleactionItemDetails?.data?.target,
        description: singleactionItemDetails?.data?.description,
        priority: singleactionItemDetails?.data?.priority,
        due_date: singleactionItemDetails?.due_date,
        assignee: singleactionItemDetails?.assignee,
        owner_fullname: singleactionItemDetails?.owner_fullname,
        created_at: singleactionItemDetails?.created_at,
        current_status: singleactionItemDetails?.current_status,
        status_log: singleactionItemDetails?.status_log,
        snooze_end: singleactionItemDetails?.snooze_end,
      });
    // eslint-disable-next-line
  }, [singleactionItemDetails]);

  const [statusActionDate, setStatusActionDate] = useState(
    singleActionItemData?.snooze_end
  );

  useEffect(() => {
    (() => {
      if (
        !singleActionItemData?.status_log ||
        !singleActionItemData?.current_status
      )
        return;
      if (singleActionItemData?.current_status === "Snooze") {
        setStatusActionDate(
          dayjs(singleActionItemData?.snooze_end).format("YYYY-MM-DD HH:mm:ss")
        ); // Removes time portion
      } else {
        const lastModified = singleActionItemData?.status_log.reduce(
          (latest: any, entry: any) =>
            new Date(entry.modified_at) > new Date(latest.modified_at)
              ? entry
              : latest
        );
        setStatusActionDate(lastModified.modified_at.split("T")[0]); // Ensures consistency
      }
    })();
    // eslint-disable-next-line
  }, [singleActionItemData?.current_status, singleActionItemData?.status_log]);

  // Set editable data on editActionItemData state
  useEffect(() => {
    if (singleActionItemData && Object.keys(singleActionItemData).length > 0) {
      setEditActionItemData({
        target: singleActionItemData.target,
        priority: singleActionItemData.priority,
        due_date: singleActionItemData.due_date,
        assignee: singleActionItemData.assignee,
        current_status: singleActionItemData.current_status,
        description: singleActionItemData.description,
        statusActionDate: statusActionDate
          ? dayjs(statusActionDate).format("YYYY-MM-DD HH:mm:ss")
          : singleActionItemData?.snooze_end,
      });
    }
    //eslint-disable-next-line
  }, [singleActionItemData]);

  //-- this description coming from redux when change on description field data set in redux
  useEffect(() => {
    if (
      description !== undefined ||
      description !== null ||
      description !== ""
    ) {
      setEditActionItemData((prevData: any) => ({
        ...prevData,
        description: description,
      }));
    } else {
      setEditActionItemData((prevData: any) => ({
        ...prevData,
        description: "",
      }));
    }
  }, [description]);

  //-- Get site name
  for (const item of websitePermissions) {
    if (item[singleActionItemData?.reference_id]) {
      siteName = item[singleActionItemData?.reference_id].ui_site_name;
      break;
    }
  }

  //-- Handle Input Item Change
  const handleActionItemChange = (name: string, value: any) => {
    setEditActionItemData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    let errorMsg = "";
    if (name === "target") {
      errorMsg = validateText(value);
    } else if (name === "due_date") {
      errorMsg = validateDateFormat(value);
    } else if (name === "assignee") {
      errorMsg = isEmpty(value);
    } else if (name === "priority") {
      errorMsg = isEmpty(value);
    } else if (name === "current_status") {
      errorMsg = isEmpty(value);
    } else if (name === "statusActionDate") {
      errorMsg = validateDateFormat(value);
    }

    setFormError((prevErrors: any) => ({
      ...prevErrors,
      [name]: errorMsg,
    }));
  };

  //-- clicking on save changes button to update single task details
  const handleUpdateActionItem = async () => {
    const updateRequestBody = {
      assignee: editActionItemData?.assignee,
      current_status: editActionItemData?.current_status,
      due_date: editActionItemData?.due_date,
      snooze_end: dayjs(statusActionDate).format("YYYY-MM-DD HH:mm:ss"),
      data: {
        description: description,
        priority: editActionItemData?.priority,
        category: singleActionItemData?.category,
        target: editActionItemData?.target,
        action_item: singleActionItemData?.action_item,
      },
    };

    try {
      setIsLoading(true);
      const res = await ActionItemsEndPointLogic.updateActionItemData(
        oauthTokenHeader,
        updateRequestBody,
        singleActionItemData?.shared_uid
      );

      if (res?.reason !== "Multi status.") {
        throw new Error(
          `Failed to update status for ${singleActionItemData?.action_item}`
        );
      } else {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Action item has been updated",
            type: "success",
          })
        );
        navigate(`/assets/overview/action_items${location.search}`); // Redirect to the Action Items List
      }
    } catch (error) {
      dispatch({ type: "error", payload: { message: error } });
    } finally {
      setIsLoading(false);
    }
  };

  //-- Check if form is valid (all fields must pass validation)
  const isFieldValid = (value: any) =>
    value !== null && value !== undefined && value.toString().trim() !== "";
  const validateForm = (data: any) => {
    if (Object.values(formError).some((err) => err)) return false; // Check for errors

    if (!isFieldValid(data.description)) return true; // skip description

    if (
      data.current_status !== "Snooze" &&
      !isFieldValid(data.statusActionDate)
    )
      return true; // Conditionally validate statusActionDate if current_status is "Snooze"

    return true;
  };
  const isFormValid = validateForm(editActionItemData);

  return (
    <Paper sx={{ padding: "8px 16px" }}>
      <Typography fontSize={24}>
        {singleActionItemData?.action_item
          ? capitalizeEachWords(singleActionItemData?.action_item)
          : ""}
      </Typography>
      <Grid container spacing={2} mt={1}>
        <Grid size={8}>
          <Grid container flexDirection={"row"} spacing={2}>
            <Grid size={4}>
              <Stack gap={2}>
                <Box>
                  <Typography>Category</Typography>
                  <Typography fontWeight={500}>
                    {singleActionItemData?.category
                      ? capitalizeEachWords(singleActionItemData?.category)
                      : ""}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Target"
                  size="small"
                  color="secondary"
                  placeholder="Specify your target for this item"
                  required
                  value={editActionItemData?.target}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleActionItemChange("target", e.target.value)
                  }
                  error={formError?.target}
                  helperText={formError?.target}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Due Date"
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        error: !!formError?.due_date,
                        helperText: formError?.due_date,
                        size: "small",
                        color: "secondary",
                        InputLabelProps: { shrink: true },
                        placeholder: "Pick a date",
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => {
                            // Block ALL keys including numbers and delete
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyPress: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyUp: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onBeforeInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onPaste: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onCut: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onChange: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onFocus: (e) => {
                            e.target.blur(); // Remove focus immediately
                          },
                          style: {
                            pointerEvents: "none",
                            caretColor: "transparent",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            MozUserSelect: "none",
                            cursor: "default",
                          },
                        },
                        sx: {
                          "& .MuiInputBase-input": {
                            pointerEvents: "none !important",
                            caretColor: "transparent !important",
                            userSelect: "none !important",
                            WebkitUserSelect: "none !important",
                            MozUserSelect: "none !important",
                            "&:focus": {
                              outline: "none !important",
                              boxShadow: "none !important",
                            },
                          },
                          "& .MuiIconButton-root": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        },
                      },
                    }}
                    format="DD/MM/YYYY"
                    value={
                      editActionItemData?.due_date
                        ? dayjs(editActionItemData?.due_date)
                        : null
                    }
                    onChange={(date) => {
                      handleActionItemChange(
                        "due_date",
                        dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                      );
                    }}
                    shouldDisableDate={(date) =>
                      statusActionDate &&
                      (dayjs(date).isBefore(dayjs(statusActionDate), "day") ||
                        dayjs(date).isSame(dayjs(statusActionDate), "day"))
                    } //due_date should be greater or not equal than statusActionDate if not then disabled
                  />
                </LocalizationProvider>
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack gap={2}>
                <Box>
                  <Typography>Asset</Typography>
                  <Typography fontWeight={500}>
                    {siteName ? capitalizeEachWords(siteName) : ""}
                  </Typography>
                </Box>
                <Autocomplete
                  options={PriorityOptions}
                  fullWidth
                  getOptionLabel={(option) => option?.label}
                  value={
                    PriorityOptions.find(
                      (opt) => opt.label === editActionItemData?.priority
                    ) || null
                  }
                  onChange={(_, newValue: any) =>
                    handleActionItemChange("priority", newValue?.label || null)
                  }
                  renderOption={(props, option) => (
                    <ListItem {...props} key={option.label}>
                      <ListItemIcon>{option.icon}</ListItemIcon>
                      <ListItemText primary={option.label} />
                    </ListItem>
                  )}
                  renderInput={(params) => {
                    // Find the selected option to show the correct icon
                    const selectedOption = PriorityOptions.find(
                      (opt) => opt.label === params.inputProps.value
                    );
                    return (
                      <TextField
                        {...params}
                        label="Priority"
                        size="small"
                        color="secondary"
                        required
                        error={formError?.priority}
                        helperText={formError?.priority}
                        placeholder="Choose from the list."
                        sx={{ input: { cursor: "pointer" } }} // show pointer on hover
                        onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: selectedOption ? (
                            <InputAdornment position="start">
                              {selectedOption.icon}
                            </InputAdornment>
                          ) : null,
                        }}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                      />
                    );
                  }}
                />
                <Autocomplete
                  disablePortal={false}
                  disabled={
                    !statusActionDate || !!validateDateFormat(statusActionDate)
                  }
                  size="small"
                  options={["In Progress", "Snooze", "Done", "To Do"]}
                  getOptionLabel={(option) => option}
                  onChange={(_: unknown, newValue: any) => {
                    handleActionItemChange("current_status", newValue || null);
                  }}
                  value={
                    ["In Progress", "Snooze", "Done", "To Do"].find(
                      (option: any) =>
                        option?.toLowerCase() ===
                        editActionItemData?.current_status?.toLowerCase()
                    ) || null
                  }
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Status"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      color="secondary"
                      required
                      error={formError?.current_status}
                      helperText={formError?.current_status}
                      sx={{ input: { cursor: "pointer" } }} // show pointer on hover
                      onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack gap={2}>
                <Box>
                  <Typography>Owner</Typography>
                  <Typography fontWeight={500}>
                    {singleActionItemData?.owner_fullname
                      ? capitalizeEachWords(singleActionItemData.owner_fullname)
                      : "N/A"}
                  </Typography>
                </Box>
                <Autocomplete
                  disablePortal={false}
                  options={allUsers}
                  getOptionLabel={(option) =>
                    `${option?.first_name || ""} ${option?.last_name || ""}`
                  } // Display name correctly
                  value={
                    allUsers?.find(
                      (user: any) => user?.id === editActionItemData?.assignee
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    handleActionItemChange("assignee", newValue?.id || null)
                  }
                  fullWidth
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={option.id}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Avatar
                        src={option?.avatarUrl} // Ensure this field exists in your user data
                        sx={{ width: 24, height: 24, fontSize: 12 }}
                      >
                        {option.first_name.charAt(0).toUpperCase()}
                      </Avatar>
                      {option.first_name} {option.last_name}
                    </li>
                  )}
                  renderInput={(params) => {
                    const selectedUser = params.inputProps.value
                      ? allUsers.find(
                          (user: any) =>
                            `${user.first_name} ${user.last_name}` ===
                            params.inputProps.value
                        )
                      : null;

                    return (
                      <TextField
                        {...params}
                        label="Assignee"
                        size="small"
                        required
                        aria-readonly
                        error={formError?.assignee}
                        helperText={formError?.assignee}
                        color="secondary"
                        placeholder="Choose Stakeholder from the list"
                        sx={{ input: { cursor: "pointer" } }} // show pointer on hover
                        onKeyDown={(e) => e.preventDefault()} // Blocks manual typing
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: selectedUser ? (
                            <Avatar
                              src={selectedUser.avatarUrl}
                              sx={{ width: 24, height: 24, marginRight: 1 }}
                            >
                              {selectedUser.first_name.charAt(0).toUpperCase()}
                            </Avatar>
                          ) : null,
                        }}
                      />
                    );
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={(() => {
                      if (editActionItemData?.current_status === "Snooze") {
                        return "Until";
                      } else if (
                        editActionItemData?.current_status === "In Progress"
                      ) {
                        return "Since";
                      } else {
                        return "On";
                      }
                    })()}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        error: !!formError?.statusActionDate,
                        helperText: formError?.statusActionDate,
                        size: "small",
                        color: "secondary",
                        InputLabelProps: { shrink: true },
                        placeholder: "Pick a date",
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => {
                            // Block ALL keys including numbers and delete
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyPress: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onKeyUp: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onBeforeInput: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onPaste: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onCut: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onChange: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                          },
                          onFocus: (e) => {
                            e.target.blur(); // Remove focus immediately
                          },
                          style: {
                            pointerEvents: "none",
                            caretColor: "transparent",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            MozUserSelect: "none",
                            cursor: "default",
                          },
                        },
                        sx: {
                          "& .MuiInputBase-input": {
                            pointerEvents: "none !important",
                            caretColor: "transparent !important",
                            userSelect: "none !important",
                            WebkitUserSelect: "none !important",
                            MozUserSelect: "none !important",
                            "&:focus": {
                              outline: "none !important",
                              boxShadow: "none !important",
                            },
                          },
                          "& .MuiIconButton-root": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        },
                      },
                    }}
                    format="DD/MM/YYYY"
                    minDate={dayjs()} // Prevent selection of past dates
                    disablePast // Disables all past dates automatically
                    value={dayjs(statusActionDate)}
                    onChange={(date) => {
                      setStatusActionDate(dayjs(date).format("YYYY-MM-DD"));
                      const errorMsg = validateDateFormat(date);
                      setFormError((prevErrors: any) => ({
                        ...prevErrors,
                        statusActionDate: errorMsg,
                      }));
                    }}
                    shouldDisableDate={(date) =>
                      editActionItemData?.due_date &&
                      (dayjs(date).isAfter(
                        dayjs(editActionItemData.due_date),
                        "day"
                      ) ||
                        dayjs(date).isSame(
                          dayjs(editActionItemData.due_date),
                          "day"
                        ) ||
                        dayjs(date).isBefore(dayjs(), "day"))
                    } // Snooze End must be before or not equal to Due Date if not then disabled
                    disabled={
                      editActionItemData?.current_status !== "Snooze" ||
                      dayjs(statusActionDate).isAfter(
                        dayjs(editActionItemData.due_date),
                        "day"
                      ) ||
                      dayjs(statusActionDate).isSame(
                        dayjs(editActionItemData.due_date),
                        "day"
                      )
                    }
                  />
                </LocalizationProvider>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={4} display="flex" flexDirection="column">
          <Stack
            gap={2}
            flexDirection="row"
            width="100%"
            height="100%"
            justifyContent="space-between"
          >
            <Stack justifyContent={"space-between"}>
              <Box>
                <Typography>Created on</Typography>
                <Typography fontWeight={500}>
                  {singleActionItemData?.created_at
                    ? dayjs(singleActionItemData?.created_at).format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Typography>
              </Box>
              <Button
                color="secondary"
                sx={{ cursor: "pointer" }}
                onClick={() => setOpen(true)}
              >
                Status History log
              </Button>
            </Stack>
            <Stack gap={2} alignItems="flex-end" justifyContent={"flex-end"}>
              <Button
                data-tracker="111"
                variant="contained"
                color="secondary"
                loading={isLoading}
                loadingPosition="end"
                endIcon={isLoading && <SaveIcon />}
                disabled={!isFormValid}
                onClick={handleUpdateActionItem}
              >
                <Stack alignItems="center" gap={1} direction="row">
                  Save changes <SaveOutlinedIcon fontSize="small" />
                </Stack>
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      <StatusHistoryLog open={open} onClose={() => setOpen(false)} />
    </Paper>
  );
};

export default QualityScore;
